"use client";

import React from "react";
import * as Portal from "@radix-ui/react-portal";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "../ui/Button";
import { Container } from "../Container";
import LoadingSpinner from "../LoadingSpinner";
import Strings from "@/shared/utils/Strings.constants";
import { cn } from "@/shared/utils";
import { useFormMethodsContext } from "../Form";

/**
 * ENG-17301 — "Proceed to Checkout" CTA for the coverage-step test.
 *
 * Both variants submit the step through `formMethods.onFormContinue`, which is the exact handler
 * behind the footer CTA (`Form`'s `handleContinue`), so validation, loading state and step
 * navigation stay in one place.
 */
const useCheckoutCta = () => {
    const formMethods = useFormMethodsContext();
    const isValidating = !!formMethods?.isValidating;
    const canContinue = formMethods?.canContinue ?? true;

    return {
        isBusy: !canContinue || isValidating,
        onCheckout: async () => {
            await formMethods?.onFormContinue?.();
        }
    };
};

/**
 * Variant 1's pill, sized to sit in the sticky menu's button slot (28px tall, 12px label) per the
 * Figma spec. The sticky menu owns its enter/exit animation.
 */
export const StickyCheckoutCtaButton = ({ className, formId, isHidden = false }: { className?: string; formId?: string; isHidden?: boolean }) => {
    const { isBusy, onCheckout } = useCheckoutCta();

    return (
        <Button
            // Form-scoped to match every other step CTA (`${FORM_ID}|...`), so the existing
            // DataDog dashboards that filter on that prefix pick these clicks up.
            data-dd-action-name={`${formId}|${Strings.DATA_DOG.COVERAGE_STICKY_CHECKOUT_CTA}`}
            data-testid="sticky-menu-checkout-cta"
            variant="default-rounded"
            size="xs"
            onClick={onCheckout}
            // `isHidden` covers the faded-out half of the sticky-menu swap: it stays mounted to hold
            // the slot's size, so it must not be clickable or tabbable while invisible.
            disabled={isBusy || isHidden}
            tabIndex={isHidden ? -1 : undefined}
            decoratorWrapperClass="gap-1"
            endDecorator={isBusy ? <LoadingSpinner size="xs" /> : <FontAwesomeIcon icon={faChevronRight} className="size-4" />}
            className={cn("whitespace-nowrap text-xs font-medium leading-4 shadow-sm", className)}
        >
            {Strings.PROCEED_TO_CHECKOUT}
        </Button>
    );
};

/**
 * Variant 2 — the same CTA pinned to the bottom of the viewport. Portalled so no scroll container
 * on the step can clip it, and sat at z-30: above the sticky menu (z-25) but below the toasts
 * (z-45/z-50) that are allowed to cover it.
 */
export const FloatingCheckoutCta = ({ isVisible, formId }: { isVisible: boolean; formId?: string }) => {
    const { isBusy, onCheckout } = useCheckoutCta();

    return (
        <Portal.Root>
            <AnimatePresence>
                {isVisible && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 16 }}
                        transition={{ duration: 0.2 }}
                        // The gradient strip spans the viewport, so it must not swallow clicks on
                        // the content it fades over — only the button itself is interactive.
                        // Vertical padding only: Container supplies the 16px side inset the design calls for.
                        className="from-transparent pointer-events-none fixed inset-x-0 bottom-0 z-30 bg-gradient-to-b to-background-secondary py-4"
                        data-testid="floating-checkout-cta"
                    >
                        <Container>
                            <div className="flex justify-center">
                                <Button
                                    data-dd-action-name={`${formId}|${Strings.DATA_DOG.COVERAGE_FLOATING_CHECKOUT_CTA}`}
                                    data-testid="floating-checkout-cta-button"
                                    variant="default-rounded"
                                    size="md"
                                    onClick={onCheckout}
                                    disabled={isBusy}
                                    endDecorator={isBusy ? <LoadingSpinner size="sm" /> : <FontAwesomeIcon icon={faChevronRight} className="size-4" />}
                                    className="pointer-events-auto w-full whitespace-nowrap shadow-sm sm:w-auto"
                                >
                                    {Strings.PROCEED_TO_CHECKOUT}
                                </Button>
                            </div>
                        </Container>
                    </motion.div>
                )}
            </AnimatePresence>
        </Portal.Root>
    );
};
